package er.prateek.crossly.content

import er.prateek.crossly.data.model.Clue
import er.prateek.crossly.data.model.Puzzle

/**
 * Why a puzzle can't be shown to a player. Kept as a value rather than a bare
 * boolean so tests can report *what* is wrong instead of just that something is.
 */
enum class PuzzleDefect {
    /** The same answer appears more than once -- e.g. SUN across and SUN down. */
    REPEATED_ANSWER,

    /** Two crossing answers disagree about the letter in the shared cell. */
    CROSSING_CONFLICT,

    /** An answer runs off the grid, or onto a black square. */
    ANSWER_OFF_GRID,

    /** No clues at all, or an answer/clue with no text. */
    EMPTY_CONTENT,
}

/**
 * Structural checks a puzzle must pass before it reaches the board, whether it
 * was hand-authored or generated. [er.prateek.crossly.game.crossword.CrosswordEngine]
 * treats the clue list as the source of truth for the solution grid, so a
 * puzzle failing these isn't merely untidy -- a crossing conflict makes it
 * literally unsolvable.
 */
fun Puzzle.defects(): Set<PuzzleDefect> {
    val defects = mutableSetOf<PuzzleDefect>()
    val clues = cluesAcross + cluesDown

    if (clues.isEmpty() || clues.any { it.answer.isBlank() || it.clue.isBlank() }) {
        defects.add(PuzzleDefect.EMPTY_CONTENT)
    }

    val answers = clues.map { it.answer }
    if (answers.size != answers.toSet().size) defects.add(PuzzleDefect.REPEATED_ANSWER)

    val solution = mutableMapOf<Pair<Int, Int>, Char>()

    fun lay(clue: Clue, rowStep: Int, colStep: Int) {
        for ((i, char) in clue.answer.withIndex()) {
            val row = clue.row + rowStep * i
            val col = clue.col + colStep * i
            if (row !in 0 until height || col !in 0 until width || grid[row][col] == '#') {
                defects.add(PuzzleDefect.ANSWER_OFF_GRID)
                continue
            }
            val existing = solution.put(row to col, char)
            if (existing != null && existing != char) defects.add(PuzzleDefect.CROSSING_CONFLICT)
        }
    }

    cluesAcross.forEach { lay(it, rowStep = 0, colStep = 1) }
    cluesDown.forEach { lay(it, rowStep = 1, colStep = 0) }

    return defects
}

fun Puzzle.isWellFormed(): Boolean = defects().isEmpty()
